import { Injectable } from '@angular/core';
import {
  Observable,
  switchMap,
  of
} from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import { ClientReferralMember, IClientReferralMemberApiResponse } from '@sondermind/utilities/models-clinical';
import {
  IAPIFetchResponse,
  IAPIFetchRequestOptions,
  IAPIListRequestOptions,
  IAPIListResponse
} from '@sondermind/utilities/models-http';

import { ReferralSourceEnum } from '@sondermind/utilities/models-referrals';
import { ClientReferralHttpService } from './client-referral.service';

@Injectable({
  providedIn: 'root'
})
export class ClientReferralMemberService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService,
    private clientReferralService: ClientReferralHttpService
  ) { }

  get indexUrl(): string {
    return `${this.configurationService.env.svcBase}/referrals/client_referral_members`;
  }

  private withClientReferral$(
    member: ClientReferralMember,
    view: string = ''
  ): Observable<ClientReferralMember> {
    const { clientReferralId } = member;

    return this.clientReferralService.getClientReferral$(clientReferralId, { view })
      .pipe(
        map((clientReferralResp) => {
          // eslint-disable-next-line no-param-reassign
          member.clientReferral = clientReferralResp.data;
          return member;
        })
      );
  }

  getMember$(
    referralMemberId: number,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<IAPIFetchResponse<ClientReferralMember> | null> {
    const url = `${this.indexUrl}/${referralMemberId}`;
    const options = this.http.generateFetchParams(opts);

    return this.http
      .get<IAPIFetchResponse<IClientReferralMemberApiResponse>>(url, options)
      .pipe(
        switchMap((res) => this.withClientReferral$(new ClientReferralMember(res.data), 'account_info').pipe(
          map((updatedMember) => ({ ...res, data: updatedMember }))
        )),
        this.http.handleHttpError(opts.error)
      );
  }

  accept$(
    referralMemberId: number,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<IAPIFetchResponse<ClientReferralMember> | null> {
    const url = `${this.indexUrl}/${referralMemberId}/accept`;
    const options = this.http.generateFetchParams(opts);

    return this.http
      .post<IAPIFetchResponse<IClientReferralMemberApiResponse>>(url, options)
      .pipe(
        switchMap((res) => {
          const member = new ClientReferralMember(res.data);
          return this.withClientReferral$(member).pipe(
            switchMap((updatedMember) => {
              if (updatedMember.clientReferral?.referralSource === ReferralSourceEnum.INTERNAL_REFERRAL) {
                return this.clientReferralService.getInternalReferralDetails$(updatedMember.clientReferral.id).pipe(
                  map((internalDetails) => {
                    // eslint-disable-next-line no-param-reassign
                    updatedMember.internalReferralDetail = internalDetails.data;
                    return { ...res, data: updatedMember };
                  })
                );
              }
              return of({ ...res, data: updatedMember });
            })
          );
        }),
        this.http.handleHttpError(opts.error)
      );
  }

  decline$(
    referralMemberId: number,
    providerRematchReasonId: number,
    otherReason?: string,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<IAPIFetchResponse<ClientReferralMember> | null> {
    const url = `${this.indexUrl}/${referralMemberId}/decline`;
    const options = this.http.generateFetchParams(opts);
    const body = {
      provider_rematch_reason_id: providerRematchReasonId,
      other_reason: otherReason
    };

    return this.http
      .post<IAPIFetchResponse<IClientReferralMemberApiResponse>>(url, body, options)
      .pipe(
        switchMap((res) => this.withClientReferral$(new ClientReferralMember(res.data)).pipe(
          map((updatedMember) => ({ ...res, data: updatedMember }))
        )),
        this.http.handleHttpError(opts.error)
      );
  }

  index$(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<ClientReferralMember> | null> {
    const url = this.indexUrl;
    const params = this.http.generateListParams(opts);

    return this.http
      .get<IAPIListResponse<IClientReferralMemberApiResponse>>(url, params)
      .pipe(
        switchMap((resp) => {
          const members = resp.data.map((res) => new ClientReferralMember(res));
          const clientReferralIds = members.map((member) => member.clientReferralId);
          const filterOptions = {
            filter: [{ key: 'ids', value: clientReferralIds.join(',') }]
          };

          return this.clientReferralService.getReferrals$(filterOptions).pipe(
            map((referralsResp) => {
              const referralsMap = new Map(referralsResp.data.map((referral) => [referral.id, referral]));
              const updatedMembers = members.map((member) => {
                // eslint-disable-next-line no-param-reassign
                member.clientReferral = referralsMap.get(member.clientReferralId) || null;
                return member;
              });
              return { ...resp, data: updatedMembers };
            })
          );
        }),
        this.http.handleHttpError(opts.error)
      );
  }
}
